<!--
  BaseStatusSelect — цветной бейдж статуса с опциональным выпадающим меню для смены значения.

  Пропсы:
    value   — текущее значение (ключ статуса), нужно для фильтрации текущего из меню
    label   — отображаемый текст
    color   — цвет фона бейджа (hex или CSS-значение)
    options — список вариантов для смены статуса: [{ value, label, color }]
              Если пустой — компонент работает только как бейдж без возможности редактирования
    compact — компактный стиль для таблиц

  Цвет текста вычисляется автоматически из color через getContrastColor.

  События:
    change(value) — выбран новый статус из меню
-->
<template>
  <span class="base-status-select">
    <span
      class="base-status-badge"
      :class="{
        'base-status-badge--editable': hasOptions,
        'base-status-badge--compact': compact,
      }"
      :style="{ background: resolvedColor, color: resolvedTextColor }"
      @click.stop="hasOptions ? openMenu($event) : null"
    >
      {{ displayLabel }}
      <svg
        v-if="hasOptions"
        class="base-status-chevron"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </span>

    <BaseContextMenu
      :visible="menuVisible"
      :x="menuX"
      :y="menuY"
      :items="menuItems"
      @close="closeMenu"
    />
  </span>
</template>

<script>
import { getContrastColor } from "@/utils/colorUtils";
import { getContextMenuPosition } from "@/utils/contextMenuUtils";

export default {
  name: "BaseStatusSelect",

  props: {
    value: {
      type: String,
      default: null,
    },
    status: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      default: "#64748b",
    },
    options: {
      type: Array,
      default: () => [],
      // [{ value, label, color }]
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["change"],

  data() {
    return {
      menuVisible: false,
      menuX: 0,
      menuY: 0,
    };
  },

  computed: {
    // CSS-переменные нельзя распарсить — для них всегда белый текст
    resolvedTextColor() {
      if (this.resolvedColor.startsWith("var(")) return "#fff";
      return getContrastColor(this.resolvedColor);
    },

    hasOptions() {
      return this.options.length > 0;
    },

    displayLabel() {
      return this.status?.name || this.label || "Не указан";
    },

    resolvedColor() {
      return this.status?.color || this.color;
    },

    statusValue() {
      return this.status?.id ? String(this.status.id) : this.value;
    },

    menuItems() {
      return this.options
        .filter((opt) => opt.value !== this.statusValue)
        .map((opt) => ({
          key: opt.value,
          label: opt.label,
          icon: opt.color
            ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${opt.color};flex-shrink:0;"></span>`
            : null,
          action: () => this.$emit("change", opt.value),
        }));
    },
  },

  methods: {
    // TODO: вынести общую механику context menu в helper/composable.
    openMenu(event) {
      const { x, y } = getContextMenuPosition(event, {
        menuHeight: 44 * this.menuItems.length,
      });

      this.menuX = x;
      this.menuY = y;
      this.menuVisible = true;

      document.addEventListener("click", this.closeMenu, {
        once: true,
      });
    },

    closeMenu() {
      this.menuVisible = false;
    },
  },
};
</script>

<style scoped>
.base-status-select {
  display: inline-block;
}

.base-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  user-select: none;
}

.base-status-badge--editable {
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.base-status-badge--editable:hover {
  opacity: 0.85;
}

.base-status-badge--compact {
  padding: 4px 7px;
  font-size: 13px;
  font-weight: 400;
  border-radius: 12px;
  white-space: nowrap;
}

.base-status-chevron {
  flex-shrink: 0;
}
</style>
